import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemButton, ListItemText,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'

Index.layout = (page) => <AppShell>{page}</AppShell>

const DATA_TYPE_LABELS = {
  decimal: 'Dezimal',
  integer: 'Ganzzahl',
  string: 'Text',
}

export default function Index({ property, attribute_types, available_system_types }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}/unit_attribute_types`
  const [importOpen, setImportOpen] = useState(false)

  const handleImport = (systemTypeId) => {
    router.post(`${basePath}/import_from_system`, {
      system_attribute_type_id: systemTypeId,
    })
    setImportOpen(false)
  }

  return (
    <Box>
      <PageHeader
        title="Attributtypen"
        breadcrumbs={[
          { label: 'Attributtypen' },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {available_system_types.length > 0 && (
              <Button variant="outlined" startIcon={<DownloadIcon />}
                onClick={() => setImportOpen(true)}
                data-testid="import-btn">
                Importieren
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => router.visit(`${basePath}/new`)}
              data-testid="new-attr-type-btn">
              Neuer Attributtyp
            </Button>
          </Box>
        }
      />

      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.subtle' }}>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Slug</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Datentyp</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Pflicht (WEG)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Verteilerschlüssel</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Herkunft</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attribute_types.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Keine Attributtypen vorhanden.</Typography>
                </TableCell>
              </TableRow>
            )}
            {attribute_types.map((at) => (
              <TableRow key={at.id} hover sx={{ cursor: 'pointer' }}
                onClick={() => router.visit(`${basePath}/${at.id}`)}
                data-testid={`attr-type-row-${at.id}`}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{at.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{at.slug}</Typography>
                </TableCell>
                <TableCell>{DATA_TYPE_LABELS[at.data_type] || at.data_type}</TableCell>
                <TableCell>{at.unit_of_measure || '–'}</TableCell>
                <TableCell>
                  {at.required_for_weg
                    ? <Chip label="Ja" size="small" color="primary" variant="outlined" />
                    : '–'}
                </TableCell>
                <TableCell>
                  {at.usable_as_distribution_key
                    ? <Chip label="Ja" size="small" color="success" variant="outlined" />
                    : '–'}
                </TableCell>
                <TableCell>
                  {at.system_attribute_type_name
                    ? <Chip label="System" size="small" variant="outlined" />
                    : <Chip label="Individuell" size="small" variant="outlined" color="info" />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Systemattribut importieren</DialogTitle>
        <DialogContent>
          {available_system_types.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Alle Systemattribute wurden bereits importiert.
            </Typography>
          ) : (
            <List>
              {available_system_types.map((sat) => (
                <ListItem key={sat.id} disablePadding>
                  <ListItemButton onClick={() => handleImport(sat.id)}
                    data-testid={`import-sat-${sat.id}`}>
                    <ListItemText
                      primary={sat.name}
                      secondary={`${DATA_TYPE_LABELS[sat.data_type] || sat.data_type}${sat.unit_of_measure ? ` (${sat.unit_of_measure})` : ''} — ${sat.category || ''}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
